/**
 * Pay cycle utilities for consistent display and calculations across the application.
 *
 * Employee pay cycles are stored as: "Weekly", "Bi-weekly", "Semi-monthly", "Monthly"
 * These utilities help convert between display formats and calculation values.
 */

/**
 * The stored pay cycle values in the database
 */
export type StoredPayCycle = "Weekly" | "Bi-weekly" | "Semi-monthly" | "Monthly";

/**
 * Pay cycle values used in the premium calculation functions
 */
export type CalculationPayCycle = "weekly" | "biweekly" | "semimonthly" | "monthly";

/**
 * Maps stored pay cycle values to the number of pay periods per year
 */
export function getPayCyclePeriodsPerYear(payCycle: string | null | undefined): number {
  const normalized = normalizePayCycle(payCycle);
  const periods: Record<CalculationPayCycle, number> = {
    weekly: 52,
    biweekly: 26,
    semimonthly: 24,
    monthly: 12,
  };
  return periods[normalized];
}

/**
 * Converts a monthly premium to a per-paycheck amount based on pay cycle
 */
export function calculatePerPaycheckAmount(
  monthlyPremium: number,
  payCycle: string | null | undefined
): number {
  const periodsPerYear = getPayCyclePeriodsPerYear(payCycle);
  const annualPremium = monthlyPremium * 12;
  const perPaycheck = annualPremium / periodsPerYear;
  return Math.round(perPaycheck * 100) / 100;
}

/**
 * Annual amount payroll actually deducts: the rounded per-paycheck amount
 * x pay periods per year (not monthly x 12 — e.g. $773.43/mo semi-monthly is
 * $386.72 x 24 = $9,281.28, not $9,281.16). Monthly cycle = monthly x 12.
 */
export function calculateAnnualDeductionAmount(
  monthlyPremium: number,
  payCycle: string | null | undefined
): number {
  const annual =
    calculatePerPaycheckAmount(monthlyPremium, payCycle) * getPayCyclePeriodsPerYear(payCycle);
  return Math.round(annual * 100) / 100;
}

/**
 * Normalizes stored pay cycle values to lowercase calculation format.
 * Returns "monthly" as fallback if not set or unrecognized.
 */
export function normalizePayCycle(payCycle: string | null | undefined): CalculationPayCycle {
  if (!payCycle) return "monthly";

  const lower = payCycle.toLowerCase().replace(/[-\s]/g, "");

  switch (lower) {
    case "weekly":
      return "weekly";
    case "biweekly":
      return "biweekly";
    case "semimonthly":
      return "semimonthly";
    case "monthly":
      return "monthly";
    default:
      return "monthly";
  }
}

/**
 * Returns a display-friendly label for the pay cycle period.
 * e.g., "Bi-weekly" -> "bi-weekly", "Semi-monthly" -> "semi-monthly"
 */
export function getPayCycleLabel(payCycle: string | null | undefined): string {
  const normalized = normalizePayCycle(payCycle);

  const labels: Record<CalculationPayCycle, string> = {
    weekly: "weekly",
    biweekly: "bi-weekly",
    semimonthly: "semi-monthly",
    monthly: "monthly",
  };

  return labels[normalized];
}

/**
 * Returns a capitalized display label for the pay cycle.
 * e.g., "Bi-weekly" -> "Bi-weekly", "Semi-monthly" -> "Semi-monthly"
 */
export function getPayCycleLabelCapitalized(payCycle: string | null | undefined): string {
  const normalized = normalizePayCycle(payCycle);

  const labels: Record<CalculationPayCycle, string> = {
    weekly: "Weekly",
    biweekly: "Bi-weekly",
    semimonthly: "Semi-monthly",
    monthly: "Monthly",
  };

  return labels[normalized];
}

/**
 * Returns how many payments per period description.
 * e.g., "26 payments/year" for bi-weekly
 */
export function getPayCyclePaymentDescription(payCycle: string | null | undefined): string {
  const periods = getPayCyclePeriodsPerYear(payCycle);
  return `${periods} payments/year`;
}

/**
 * Formats premium display with pay cycle context.
 * Shows per-paycheck as primary and monthly as secondary.
 *
 * @param monthlyPremium - The monthly premium amount
 * @param payCycle - The employee's pay cycle
 * @param showMonthlySecondary - Whether to show monthly amount as secondary (default: true)
 * @returns Object with primary and secondary formatted strings
 */
export function formatPremiumWithPayCycle(
  monthlyPremium: number,
  payCycle: string | null | undefined,
  showMonthlySecondary: boolean = true
): { primary: string; secondary: string | null } {
  const normalized = normalizePayCycle(payCycle);
  const label = getPayCycleLabel(payCycle);

  // If monthly, no conversion needed
  if (normalized === "monthly") {
    return {
      primary: `$${monthlyPremium.toFixed(2)}/month`,
      secondary: null,
    };
  }

  const perPaycheck = calculatePerPaycheckAmount(monthlyPremium, payCycle);

  return {
    primary: `$${perPaycheck.toFixed(2)} ${label}`,
    secondary: showMonthlySecondary ? `($${monthlyPremium.toFixed(2)}/month)` : null,
  };
}

/**
 * Returns true if the pay cycle is set and not the default monthly.
 */
export function hasNonMonthlyPayCycle(payCycle: string | null | undefined): boolean {
  if (!payCycle) return false;
  const normalized = normalizePayCycle(payCycle);
  return normalized !== "monthly";
}
